import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  forwardRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { QuillLoaderService } from 'src/app/service/quill-loader.service';

/** alt 편집 패널 너비(px). 위치 계산(클램프)과 템플릿이 같은 값을 써야 해서 한 곳에 둔다. */
const ALT_PANEL_WIDTH = 300;
/** alt 최대 길이. 표준이 아니라 우리 기준이다 — 한 호흡에 읽히는 길이로 잡았다. */
const ALT_MAX_LENGTH = 120;
const ALT_PANEL_GAP = 8;
/** 패널이 아직 렌더되기 전(첫 계산)에 쓰는 높이 추정치(px). 렌더된 뒤에는 실측값으로 다시 계산한다. */
const ALT_PANEL_ESTIMATED_HEIGHT = 190;

// 아이콘뿐이라 이름이 통째로 비는 피커들. 제목·크기 피커는 라벨에 현재 값이 글자로 들어가
// 그것이 곧 이름이므로 건드리지 않는다 — aria-label을 붙이면 어느 단계가 켜져 있는지가 안 읽힌다.
const ICON_PICKER_LABELS: Record<string, string> = {
  'ql-color': '글자색',
  'ql-background': '형광펜',
  'ql-align': '정렬',
};

// 피커를 열면 나오는 항목도 role=button이라 이름이 필요하다. 정렬은 값이 넷뿐이고,
// 색상은 수십 개라 값(#rrggbb)을 그대로 읽힌다.
const ALIGN_ITEM_LABELS: Record<string, string> = {
  '': '왼쪽 정렬',
  center: '가운데 정렬',
  right: '오른쪽 정렬',
  justify: '양쪽 정렬',
};

@Component({
  selector: 'dynamic-quill-editor',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './dynamic-quill.component.html',
  styleUrls: ['./dynamic-quill.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DynamicQuillComponent),
      multi: true,
    },
  ],
})
export class DynamicQuillComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @ViewChild('editorContainer', { static: true }) editorContainer!: ElementRef;
  @ViewChild('editorWrap', { static: true }) editorWrap!: ElementRef<HTMLElement>;
  @ViewChild('toolbar', { static: false }) toolbar?: ElementRef<HTMLElement>;
  @ViewChild('altPanel') altPanel?: ElementRef<HTMLElement>;
  @ViewChild('altInput') altInput?: ElementRef<HTMLInputElement>;

  @Input() placeholder = '내용을 입력하세요...';
  @Input() showToolbar = true;
  @Input() editorStyle = '-webkit-user-select: text';
  @Input() options: any = {};
  /** 본문 에디터 여섯 화면이 모두 이 구성을 쓴다 — 화면마다 배열을 복사하면 조용히 갈라진다. */
  @Input() toolbarOptions: string[] = [
    'header',
    'size',
    'bold',
    'strike',
    'color',
    'background',
    'link',
    'image',
    'video',
    'list-ordered',
    'list-bullet',
    'align',
    'clean',
  ];

  @Output() onEditorCreated = new EventEmitter<any>();
  @Output() onContentChanged = new EventEmitter<any>();

  toolbarId = `toolbar-${Math.random().toString(36).substr(2, 9)}`;

  /** alt 편집 패널이 붙어 있는 이미지. null이면 패널이 닫힌 상태. */
  altTarget: HTMLImageElement | null = null;
  altDraft = '';
  altError = '';
  altPanelTop = 0;
  altPanelLeft = 0;
  isDisabled = false;
  readonly altPanelWidth = ALT_PANEL_WIDTH;
  readonly altMaxLength = ALT_MAX_LENGTH;
  readonly altInputId = `alt-${this.toolbarId}`;
  readonly altLabelId = `alt-label-${this.toolbarId}`;
  readonly altHelpId = `alt-help-${this.toolbarId}`;

  private quillInstance: any;
  private onChange = (value: string) => {};
  private onTouched = () => {};
  private destroyed = false;
  private pendingValue: string | null = null;
  /** 아직 로드가 끝나지 않아 위치 재계산을 예약해 둔 이미지. 떼어내기 위해 들고 있는다. */
  private altImageAwaitingLoad: HTMLImageElement | null = null;

  constructor(private quillLoaderService: QuillLoaderService) {}

  async ngOnInit() {
    await this.initializeQuill();
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.teardownListeners();
    this.quillInstance?.off('text-change');
    this.quillInstance?.off('selection-change');
    this.quillInstance = null;
  }

  private async initializeQuill() {
    try {
      const quillOptions = {
        ...this.options,
        placeholder: this.placeholder,
        readOnly: this.isDisabled,
        modules: {
          ...(this.options?.modules || {}),
          ...(this.options?.modules?.toolbar === undefined
            ? { toolbar: this.showToolbar ? `#${this.toolbarId}` : false }
            : {}),
        },
      };

      const instance = await this.quillLoaderService.createQuillInstance(
        this.editorContainer.nativeElement,
        quillOptions,
      );

      // await 사이에 파괴됐다면 ngOnDestroy의 teardown은 이미 지나갔다 — 여기서 리스너를 붙이면
      // document/window에 죽은 컴포넌트의 리스너가 영구히 남는다.
      if (this.destroyed) return;
      this.quillInstance = instance;

      // Set up event listeners
      this.quillInstance.on('text-change', () => {
        const content = this.quillInstance.root.innerHTML;
        this.onChange(content);
        this.onContentChanged.emit({
          html: content,
          text: this.quillInstance.getText(),
          delta: this.quillInstance.getContents(),
        });
      });

      this.quillInstance.on('selection-change', () => {
        this.onTouched();
      });

      // 본문 이미지 클릭 → alt 편집. 패널은 래퍼 기준 absolute인데 위치 계산이 window.innerHeight로
      // 클램프되므로(초장축 이미지 대응) 에디터 내부 스크롤뿐 아니라 페이지 스크롤에서도 다시 계산해야 한다.
      // 페이지 스크롤은 버블링하지 않아 캡처 단계로 받는다.
      this.quillInstance.root.addEventListener('click', this.onEditorClick);
      this.quillInstance.root.addEventListener('scroll', this.updateAltPanelPosition);
      document.addEventListener('mousedown', this.onDocumentMouseDown);
      window.addEventListener('scroll', this.updateAltPanelPosition, { capture: true, passive: true });
      window.addEventListener('resize', this.updateAltPanelPosition);

      // Apply any pending value
      if (this.pendingValue !== null) {
        const delta = this.quillInstance.clipboard.convert(this.pendingValue);
        this.quillInstance.setContents(delta, 'silent');
        this.pendingValue = null;
      }

      this.onEditorCreated.emit(this.quillInstance);

      // 값 배선이 모두 끝난 뒤에 붙인다 — 라벨 부착이 실패해도 본문이 폼에 안 들어가는 일은 없어야 한다.
      try {
        this.labelToolbarPickers();
      } catch (error) {
        console.error('툴바 접근성 라벨을 붙이지 못했습니다:', error);
      }
    } catch (error) {
      // 여기서 실패하면 사용자에게는 빈 div만 남는다 — 최소한 원인을 추적할 수 있게 남긴다.
      console.error('Failed to initialize Quill editor:', error);
    }
  }

  private teardownListeners(): void {
    this.quillInstance?.root?.removeEventListener('click', this.onEditorClick);
    this.quillInstance?.root?.removeEventListener('scroll', this.updateAltPanelPosition);
    document.removeEventListener('mousedown', this.onDocumentMouseDown);
    window.removeEventListener('scroll', this.updateAltPanelPosition, { capture: true } as EventListenerOptions);
    window.removeEventListener('resize', this.updateAltPanelPosition);
    this.forgetPendingImageLoad();
  }

  // --- 이미지 alt 편집 ---

  /**
   * 이미지의 alt 편집 패널을 연다. 업로드 직후 자동으로 열기 위해 `FileUploadService.fileChanged`도
   * 이 메서드를 호출한다.
   */
  openAltEditor(img: HTMLImageElement): void {
    // 다른 에디터(또는 Quill이 소유하지 않은 노드)의 이미지를 받으면 applyAlt가 남의 문서를 포맷하게 된다.
    if (this.isDisabled || !img || !this.quillInstance?.root?.contains(img)) return;

    // 같은 이미지를 다시 누른 경우엔 초안을 유지한다 — 아래에서 속성값으로 되돌리면
    // 취소·ESC가 아닌 경로로 입력이 사라진다.
    const sameTarget = this.altTarget === img;

    // 다른 이미지로 넘어가는 것도 "취소가 아닌 닫힘"이다 — 이전 입력을 먼저 저장한다.
    if (this.altTarget && !sameTarget) {
      if (!this.quillInstance.root.contains(this.altTarget)) {
        // 대상이 본문에서 사라졌으면 저장할 곳이 없다. 붙잡아두면 죽은 노드에 묶여 이후 어떤 이미지도
        // 열리지 않고, 안내 문구가 가리키는 "다시 클릭"조차 막힌다.
        this.closeAltEditor();
      } else {
        // 대상이 아직 있으면 같은 이미지를 다시 눌러 재시도할 수 있으므로 붙잡는 쪽이 맞다.
        this.commitAltEditor();
        if (this.altError) return;
      }
    }

    this.altTarget = img;
    if (!sameTarget) this.altDraft = img.getAttribute('alt') ?? '';
    this.altError = '';
    this.updateAltPanelPosition();

    // 업로드 직후 자동으로 열리는 경우 이미지가 아직 로드 전이라 높이가 0이다 — 그 좌표에 그대로 두면
    // 패널이 이미지를 덮는다. 로드되어 제 높이를 찾으면 다시 계산한다.
    this.forgetPendingImageLoad();
    if (!img.complete) {
      this.altImageAwaitingLoad = img;
      img.addEventListener('load', this.onAltImageLoad, { once: true });
    }

    // *ngIf가 렌더된 다음에야 패널과 input이 존재한다 — 실제 패널 높이로 위치를 한 번 더 잡는다.
    setTimeout(() => {
      this.updateAltPanelPosition();
      this.altInput?.nativeElement.focus();
    });
  }

  /** 입력을 버리고 닫는다. 명시적 취소(취소 버튼·ESC) 전용. */
  closeAltEditor(): void {
    const img = this.altTarget;
    this.altTarget = null;
    this.altDraft = '';
    this.altError = '';
    this.forgetPendingImageLoad();
    img?.focus?.();
  }

  /**
   * 사용자가 취소한 게 아닌 이유로 패널을 접어야 할 때(본문 클릭, 바깥 클릭, 이미지가 화면 밖으로 스크롤,
   * 읽기 전용 전환) 입력을 버리지 않고 저장한다. 업로드 직후 alt를 적고 본문으로 돌아가는 동선이 가장
   * 흔한데, 여기서 버리면 사용자는 자기가 쓴 문장이 사라진 줄도 모른다.
   */
  private commitAltEditor(): void {
    if (this.altTarget) this.saveAlt();
  }

  saveAlt(): void {
    const img = this.altTarget;
    if (!img) return;

    // maxlength는 타이핑만 막는다 — 기존 alt를 프리필한 경우 상한을 넘은 값이 그대로 돌아온다.
    const value = this.altDraft.trim().slice(0, ALT_MAX_LENGTH);
    if (!this.applyAlt(img, value)) {
      // 닫으면 사용자가 입력한 문장이 사라지고, 화면은 저장 성공과 구분되지 않는다.
      this.altError = '대체 텍스트를 저장하지 못했어요. 이미지를 다시 클릭해서 입력해주세요.';
      return;
    }
    this.closeAltEditor();
  }

  /**
   * 이미지에 alt를 쓴다. 빈 문자열이면 속성을 제거한다.
   *
   * quill@1.3.7의 Image blot이 alt를 네이티브 포맷으로 다루므로 별도 blot 등록이 필요 없다.
   * 제거할 때 `null`이 아니라 `false`를 넘기는 게 핵심 — Quill의 `overload()`가 `value == null`이면
   * 포맷을 통째로 버리고 name('alt')을 source로 오인해서(core/quill.js), alt가 지워지지 않는다.
   *
   * @returns 실제로 적용했으면 true. 대상 blot을 찾지 못하면(폼이 writeValue로 내용을 다시 그려
   *   Quill이 blot을 detach한 경우 등) false — 호출자는 조용히 닫지 말고 사용자에게 알려야 한다.
   */
  private applyAlt(img: HTMLImageElement, alt: string): boolean {
    if (!this.quillInstance) return false;

    const blot = this.quillLoaderService.findBlot(img);
    if (!blot) {
      console.error('alt 저장 실패: 대상 이미지의 blot을 찾지 못함', img.getAttribute('src'));
      return false;
    }

    this.quillInstance.formatText(this.quillInstance.getIndex(blot), 1, 'alt', alt.length > 0 ? alt : false, 'user');
    return true;
  }

  private forgetPendingImageLoad(): void {
    this.altImageAwaitingLoad?.removeEventListener('load', this.onAltImageLoad);
    this.altImageAwaitingLoad = null;
  }

  private readonly onAltImageLoad = (): void => {
    this.altImageAwaitingLoad = null;
    this.updateAltPanelPosition();
  };

  private readonly onEditorClick = (event: Event): void => {
    const target = event.target as HTMLElement | null;
    if (target?.tagName === 'IMG') {
      this.openAltEditor(target as HTMLImageElement);
    } else {
      this.commitAltEditor();
    }
  };

  /** 에디터·패널 바깥을 눌렀을 때 입력을 저장하고 닫는다(에디터 내부는 onEditorClick이 처리). */
  private readonly onDocumentMouseDown = (event: MouseEvent): void => {
    if (!this.altTarget) return;

    const target = event.target as Node | null;
    if (!target) return;
    if (this.altPanel?.nativeElement.contains(target)) return;
    if (this.quillInstance?.root?.contains(target)) return;

    this.commitAltEditor();
  };

  /** 리스너로 붙였다 떼야 해서 화살표 프로퍼티(안정된 참조)로 둔다. onEditorClick·onDocumentMouseDown도 같은 이유. */
  private readonly updateAltPanelPosition = (): void => {
    const img = this.altTarget;
    const wrap = this.editorWrap?.nativeElement;
    if (!img || !wrap) return;

    const imgRect = img.getBoundingClientRect();
    // 에디터가 자체 스크롤을 가질 때만 클리핑 기준이 된다(레이아웃 전이면 height가 0).
    const rootRect = (this.quillInstance?.root as HTMLElement | undefined)?.getBoundingClientRect();
    const clipRect = rootRect && rootRect.height > 0 ? rootRect : null;

    if (clipRect && (imgRect.bottom < clipRect.top || imgRect.top > clipRect.bottom)) {
      // 입력 중에는 접지 않는다 — 모바일 소프트키보드가 resize를 쏘면 타이핑 도중 패널이 사라진다.
      if (document.activeElement === this.altInput?.nativeElement) return;
      this.commitAltEditor();
      return;
    }

    // 본문 이미지는 세로 수천 px인 초장축이 흔하다. 이미지 "아래"에 그대로 두면 패널이 화면 밖에 놓여
    // 열려도 보이지 않으므로, 이미지가 실제로 보이는 구간의 아래쪽에 붙인다.
    const visibleBottom = Math.min(imgRect.bottom, clipRect?.bottom ?? Infinity, window.innerHeight);

    // 에디터 자체가 화면 아래로 넘어가 있으면 그 "아래"에도 자리가 없다 — 패널 몸통까지 뷰포트 안에
    // 들어오도록 위로 끌어올린다. 아래에 붙이는 것보다 이미지를 조금 가리는 편이 낫다.
    const panelHeight = this.altPanel?.nativeElement.offsetHeight || ALT_PANEL_ESTIMATED_HEIGHT;
    const lowestTop = Math.max(0, window.innerHeight - panelHeight - ALT_PANEL_GAP);
    const topInViewport = Math.max(0, Math.min(visibleBottom + ALT_PANEL_GAP, lowestTop));

    const wrapRect = wrap.getBoundingClientRect();
    const maxLeft = Math.max(0, wrapRect.width - ALT_PANEL_WIDTH);
    this.altPanelTop = topInViewport - wrapRect.top;
    this.altPanelLeft = Math.min(Math.max(0, imgRect.left - wrapRect.left), maxLeft);
  };

  /** Quill 초기화 뒤에만 유효하다 — 셀렉터가 빗나가면 이름 없는 피커가 조용히 남는다. */
  private labelToolbarPickers(): void {
    if (!this.showToolbar) return;

    const toolbar = this.toolbar?.nativeElement;
    if (!toolbar) {
      console.error('툴바가 아직 없어 피커 라벨을 붙이지 못했습니다.');
      return;
    }

    Object.entries(ICON_PICKER_LABELS).forEach(([pickerClass, label]) => {
      toolbar.querySelector(`.ql-picker.${pickerClass} .ql-picker-label`)?.setAttribute('aria-label', label);
    });

    toolbar.querySelectorAll('.ql-picker.ql-align .ql-picker-item').forEach((item) => {
      const value = item.getAttribute('data-value') ?? '';
      item.setAttribute('aria-label', ALIGN_ITEM_LABELS[value] ?? value);
    });

    toolbar
      .querySelectorAll('.ql-picker.ql-color .ql-picker-item, .ql-picker.ql-background .ql-picker-item')
      .forEach((item) => {
        const value = item.getAttribute('data-value');
        item.setAttribute('aria-label', value ? `색상 ${value}` : '기본 색상');
      });
  }

  // ControlValueAccessor implementation
  writeValue(value: string): void {
    const normalizedValue = value || '';

    if (this.quillInstance) {
      const currentContent = this.quillInstance.root.innerHTML;
      if (normalizedValue !== currentContent) {
        // 내용을 다시 그리면 Quill이 기존 blot을 detach한다 — 열려 있는 패널의 대상은 죽은 노드가 된다.
        // 여기서 저장해봐야 바로 아래 setContents가 덮어쓰므로 무의미하고, 폼이 값을 쓰는 도중에
        // onChange를 역방향으로 부르는 부작용만 남는다. 패널만 닫는다.
        if (this.altTarget) this.closeAltEditor();
        const delta = this.quillInstance.clipboard.convert(normalizedValue);
        this.quillInstance.setContents(delta, 'silent');
      }
    } else {
      // Store value to apply when Quill is initialized
      this.pendingValue = normalizedValue;
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    if (isDisabled) {
      this.commitAltEditor();
    }
    if (this.quillInstance) {
      this.quillInstance.enable(!isDisabled);
    }
  }

  // Public methods
  getQuill() {
    return this.quillInstance;
  }

  getContent() {
    if (this.quillInstance) {
      return {
        html: this.quillInstance.root.innerHTML,
        text: this.quillInstance.getText(),
        delta: this.quillInstance.getContents(),
      };
    }
    return null;
  }

  setContent(content: string) {
    if (this.quillInstance) {
      const delta = this.quillInstance.clipboard.convert(content);
      this.quillInstance.setContents(delta, 'silent');
    } else {
      this.pendingValue = content;
    }
  }
}
